export class Connection {
    type;

    websocket;
    connectionURL;

    heartbeatTimeout;
    pingInterval;

    connectionQueue;
    connectionRetryCount;

    pendingAcks;

    constructor() {
        this.connectionRetryCount = 0;
        this.connectionQueue = [];
        this.pendingAcks = new Map();

        this.createURL();
        this.initConnection();
    }

    setType(type) {
        this.type = type;
    }

    createURL() {
        const scheme = (location.protocol === 'https:') ? 'wss' : 'ws';
        const host = window.WS_HOST || location.hostname || 'localhost';
        let port = '';
        let separator = '';
        if (scheme === 'ws') {
            port = window.WS_PORT || 30000;
            separator = ':';
        }
        const path = window.WS_PATH || '/ws';
        this.connectionURL = `${scheme}://${host}${separator}${port}${path}`;
    }

    initConnection() {
        console.log('Tentative de connexion à : ', this.connectionURL);
        this.websocket = new WebSocket(this.connectionURL);

        this.websocket.onopen = () => this.onopen();
        this.websocket.onerror = (event) => this.onerror(event);
        this.websocket.onmessage = (event) => this.onmessage(event);
        this.websocket.onclose = (event) => this.onclose(event);
    }


    onopen() {
        console.log("Connecté à " + this.websocket.url);
        this.connectionRetryCount = 0;
        this.sendPing();

        while (this.connectionQueue.length > 0) {
            this.connectionQueue.shift()();
        }
    }

    onmessage(event) {
        console.log("Message reçu : ", event.data);

        const data = JSON.parse(event.data);

        if (data.uuid) {
            this.sendMessage({
                type: "ack",
                "reply-to": data.uuid
            });
        }

        switch (data.type) {
            case "pong":
                this.resetHeartbeat();
                break;
            case "ack":
                this.handleAck(data);
                break;
            case "error":
                this.handleError(data);
                break;

            case "checkerboardInit":
                this.handleCheckerboardInit(data);
                break;
            case "sendCode":
                this.handleCodeSend(data);
                break;
            case "move":
                this.handleMoveResult(data);
                break;

            default:
                console.warn("Type inconnu : ", data.type);
        }

    }


    onclose(event) {
        console.log("Websocket fermé : ", event);
        this.connectionRetryCount++;
        if (this.connectionRetryCount < 5) {
            console.log(`Tentative de reconnexion ${this.connectionRetryCount}/5...`);
            setTimeout(() => this.initConnection(), 2000);
        } else {
            console.error("Nombre maximum de tentatives de connexion atteint.");
            this.type.endApp('serverUnreachable');
        }
    }


    onerror(event) {
        console.log("Erreur websocket : ", event);
        this.websocket.close();
    }

    sendPing() {
        try {
            if (this.websocket.readyState === WebSocket.OPEN) {
                const message = {type: "ping"};
                this.sendMessage(message);

                this.heartbeatTimeout = setTimeout(() => {
                    console.log("pas de Pong reçu, fermeture de la connexion.");
                    try {
                        this.websocket.close();
                    } catch (e) {
                        console.error("Erreur pendant la fermeture du WebSocket")
                    }
                }, 10_000);

            }
        } catch (err) {
            console.error("sendPing error:", err);

        } finally {
            this.pingInterval = setTimeout(() => this.sendPing(), 30_000);

        }
    }

    resetHeartbeat() {
        clearTimeout(this.heartbeatTimeout);
    }

    sendMessage(message) {
        this.websocket.send(JSON.stringify(message));
        console.log("Message envoyé : ", JSON.stringify(message))
    }

    generateUUID() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    sendReliableMessage(message) {
        const uuid = this.generateUUID();
        message.uuid = uuid;

        const timer = setTimeout(() => {
            if (this.pendingAcks.has(uuid)) {
                console.warn("Message non acquitté, envoi :", message);
                this.sendMessage(message);
            }
        }, 5000);

        const ackParam = {message: message, timer: timer};
        this.pendingAcks.set(uuid, ackParam);

        this.sendMessage(message);
    }

    handleAck(data) {
        const uuid = data["reply-to"];
        if (this.pendingAcks.has(uuid)) {
            clearTimeout(this.pendingAcks.get(uuid).timer);
            this.pendingAcks.delete(uuid);
            console.log("Message acquitté :", uuid);
        } else {
            console.warn("acquittement pour un message inconnu :", uuid);
        }
    }

    handleError(data) {
        switch (data.message) {
            case "disconnected" :
                this.type.endApp("disconnected");
                break;
            default:
                console.log("Type d'erreur inconnu.");
                break;
        }
    }

    handleCheckerboardInit(data) {
        this.type.showApp(
            data.checkerboard,
            data.player,
            data.instanceId,
        );
    }

    handleCodeSend(data) {
        this.type.waitApp("En attente d'un adversaire...\n" + data.code);
    }

    handleMoveResult(data) {
        let origin = data.origin;
        let destination = data.destination;

        this.type.executeWhenAnimationReady(() => {
            this.type.controlMove(origin.x, origin.y, destination.x, destination.y);
            this.type.controlEat(destination.x, destination.y);
        });
    }

    async waitForConnection() {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            return;
        }
        return new Promise((resolve) => {
            this.connectionQueue.push(resolve);
        });
    }

    async sendAskInit(mode) {
        let message = {type: "askInit", mode: mode};

        switch (mode) {
            case 'random':
                this.type.waitApp("En attente d'un adversaire...");
                break;
            case 'private':
                const codeInput = document.getElementById("codeInput");
                message = {type: "askInit", mode: mode, code: codeInput.value};
                this.type.waitApp("En attente d'un adversaire...");
                break;
            case 'ia':
                this.type.waitApp("Chargement de l'IA...");
                break;
        }

        try {
            await this.waitForConnection();
            this.sendReliableMessage(message);
        } catch (e) {
            console.error("Erreur lors de l'envoi de askInit : " + e);
        }
    }

    async sendMove(sourceX, sourceY, destX, destY) {
        let message = {
            type: "move",
            instanceId: this.type.model.gameId,
            origin: {x: sourceX, y: sourceY},
            destination: {x: destX, y: destY}
        };


        try {
            await this.waitForConnection();
            this.sendReliableMessage(message);
        } catch (e) {
            console.error("Erreur lors de l'envoi de move : " + e);
        }
    }
}